import { ART_PALETTE } from '../art-palette.js';
import { BLOB_MASKS, MASK } from './masks.js';

/**
 * Water + sand coastline — flat hybrid house style (art-direction-rules §0).
 *
 * Water is horizontal bands only: lit crest lines (Water light 21) and depth
 * lines (Water dark 19) on a flat Water (20) body. No squiggle network, no
 * salt-and-pepper foam, no modulo pattern.
 *
 * The sand coast is one flat Sand (9) field with an Earth dark (7) contact
 * line inset on the land side (matching the shore in the style reference:
 * earth-dark line, sand band, water) and a lit shore band of Water light (21)
 * lapping the seaward edge.
 */

const WATER_BODY = 20;
const WATER_CREST = 21;
const WATER_TROUGH = 19;
const SAND = 9;
const EARTH_DARK = 7;

// ---------------------------------------------------------------------------
// Backdrop water
// ---------------------------------------------------------------------------

/**
 * Ripple bands. Long horizontal runs, never dots and never diagonals, so the
 * surface reads as banded water instead of a speckled field.
 *
 * `drift` 0 keeps a band static; static bands may sit anywhere, and the one at
 * y 13 runs through the vertical seam so the column edge is broken by a band
 * that continues in the next tile. Drifting bands stay strictly inside the
 * center 10×10 so the outer 3px seam band is identical on every frame (§9).
 */
const RIPPLE_BANDS = [
  { y: 2, x: 9, length: 7, color: WATER_CREST, drift: 0 },
  { y: 5, x: 3, length: 5, color: WATER_CREST, drift: 1 },
  { y: 9, x: 7, length: 6, color: WATER_TROUGH, drift: -1 },
  { y: 13, x: 13, length: 6, color: WATER_CREST, drift: 0 },
];

/** Ping-pong drift: every step moves one pixel, so frame 3 returns to frame 0. */
const DRIFT = [0, 1, 2, 1];

function waterFrame(phase) {
  const pixels = Array.from({ length: 16 }, () => Array(16).fill(WATER_BODY));
  for (const { y, x, length, color, drift } of RIPPLE_BANDS) {
    const start = x + drift * DRIFT[phase];
    for (let i = 0; i < length; i++) pixels[y][(start + i + 16) % 16] = color;
  }
  return pixels;
}

const WATER_FRAMES = DRIFT.map((_, phase) => waterFrame(phase));
const clonePixels = (pixels) => pixels.map((row) => row.slice());
const cloneVariants = (variants) => Object.fromEntries(
  Object.entries(variants).map(([mask, pixels]) => [mask, clonePixels(pixels)]),
);

// Guard the rule the drifting bands have to satisfy: no animated pixel may
// land in the outer 3px seam band, on any frame.
for (const { y, x, length, drift } of RIPPLE_BANDS) {
  if (!drift) continue;
  for (const step of DRIFT) {
    const start = x + drift * step;
    if (y < 3 || y > 12 || start < 3 || start + length - 1 > 12) {
      throw new Error(`backdrop.water: drifting band at y ${y} reaches the seam band`);
    }
  }
}

// ---------------------------------------------------------------------------
// Sand coastline (blob8, animated shore band)
// ---------------------------------------------------------------------------

/**
 * Palindromic inset so opposite tile edges agree in the depth-3 seam band:
 * SHORE_INSET[i] === SHORE_INSET[15 - i], which is what makes a corner join
 * compare like for like.
 *
 * The inset also moves in runs of three or more. Any line offset from this
 * profile — the contact line, the lit band — inherits its rhythm, and a profile
 * that alternates every pixel turns those lines into a dotted stitch instead of
 * a shore.
 */
const SHORE_INSET = [4, 4, 3, 3, 3, 4, 4, 4, 4, 4, 4, 3, 3, 3, 4, 4];
/** Sand band width between the waterline and the earth-dark contact line. */
const CONTACT_INSET = 5;
const NEIGHBOURS = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]];
/** Lit shore band depth per frame; ±1 per step keeps the loop continuous. */
const SHORE_BAND = [2, 3, 3, 2];

/**
 * Two-pass 8-connected distance transform. Equal to Chebyshev (chessboard)
 * distance to the nearest feature cell; feature cells themselves are 0.
 */
function chebyshevDistance(feature, width, height) {
  const INF = 127;
  const dist = new Int16Array(width * height);
  for (let i = 0; i < dist.length; i++) dist[i] = feature[i] ? 0 : INF;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      let d = dist[i];
      if (x > 0) d = Math.min(d, dist[i - 1] + 1);
      if (y > 0) {
        d = Math.min(d, dist[i - width] + 1);
        if (x > 0) d = Math.min(d, dist[i - width - 1] + 1);
        if (x + 1 < width) d = Math.min(d, dist[i - width + 1] + 1);
      }
      dist[i] = d;
    }
  }
  for (let y = height - 1; y >= 0; y--) {
    for (let x = width - 1; x >= 0; x--) {
      const i = y * width + x;
      let d = dist[i];
      if (x + 1 < width) d = Math.min(d, dist[i + 1] + 1);
      if (y + 1 < height) {
        d = Math.min(d, dist[i + width] + 1);
        if (x > 0) d = Math.min(d, dist[i + width - 1] + 1);
        if (x + 1 < width) d = Math.min(d, dist[i + width + 1] + 1);
      }
      dist[i] = d;
    }
  }
  return dist;
}

/**
 * Land occupancy, contact line, and distance-to-land for one blob8 mask.
 *
 * Both shore lines are level sets of a distance transform over the landmass.
 * That matters for continuity: an offset taken per open edge cannot express a
 * curve that has to turn on a tile whose water is only diagonal (mask 127 —
 * four cardinals connected, NW water). Such a tile has no open edge to offset
 * from, so a per-edge rule draws nothing on it and the neighbours' lines
 * terminate against it as orphan stubs. A distance level set is the boundary
 * of an eroded set, so it closes around the whole landmass, corners included.
 *
 * The distance field depends only on the mask, so it is computed once; the
 * four shore-band frames only read it.
 *
 * The probe has to look past the tile edge, and reporting a land neighbour as
 * solid everywhere is what used to bend the line one pixel at every join: a
 * land neighbour on a straight coast carries its own shore notch just past the
 * boundary. `landAt` rebuilds that notch from SHORE_INSET.
 *
 * Which of a neighbour's edges is open is read off *our diagonal bits*, not
 * off our own edges. The north neighbour's west edge faces our NW cell, so it
 * is open exactly when NW is water — and using our own `west` instead is what
 * tore the line apart at a step: a tile with west connected but NW water
 * (mask 127) reported its north neighbour as solid, missed the water sitting
 * directly above its left columns, and put its line three columns away from
 * where the neighbour above had ended. Normalisation clears a diagonal bit
 * whenever either touching cardinal is water, which is the same answer this
 * needs, so the bits can be read directly in both cases.
 *
 * The contact line is the outer layer of the landmass eroded by CONTACT_INSET,
 * drawn only where that eroded interior actually exists. On a landmass thinner
 * than twice the inset the offsets from the two shores collide and the level
 * set degenerates, so a one-tile-wide spit gets no line at all rather than a
 * row of three-pixel specks.
 *
 * Requiring the interior to be two pixels deep instead would also clear the
 * stray square a nested one-tile ring can still produce, but it breaks a
 * two-cell-wide diagonal strip into seven pieces — a far likelier shape, so
 * one pixel of interior is the right threshold.
 */
function shoreFields(mask) {
  const north = Boolean(mask & MASK.N); const east = Boolean(mask & MASK.E);
  const south = Boolean(mask & MASK.S); const west = Boolean(mask & MASK.W);
  const ne = Boolean(mask & MASK.NE); const se = Boolean(mask & MASK.SE);
  const sw = Boolean(mask & MASK.SW); const nw = Boolean(mask & MASK.NW);
  const occupiedInside = (x, y) => {
    // Same profile on every side so vertical/horizontal joins share thresholds.
    const westDepth = SHORE_INSET[y];
    const eastDepth = SHORE_INSET[y];
    const northDepth = SHORE_INSET[x];
    const southDepth = SHORE_INSET[x];
    // Connected-edge outer corners for Blob loose adjacency:
    // vertical joins share SW/SE (north tile) with W/E (south tile);
    // horizontal joins share NE/SE (west tile) with N/S (east tile).
    // Cut only the edge owned by the diagonal bits so mask 14 south matches
    // mask 1 north, without opening west/north edges that partners keep solid.
    if (south && !sw && x < westDepth && y >= 16 - southDepth) return false;
    if (south && !se && x >= 16 - eastDepth && y >= 16 - southDepth) return false;
    if (east && !ne && x >= 16 - eastDepth && y < northDepth) return false;
    if (east && !se && x >= 16 - eastDepth && y >= 16 - southDepth) return false;
    const beyondWest = !west && x < westDepth;
    const beyondEast = !east && x >= 16 - eastDepth;
    const beyondNorth = !north && y < northDepth;
    const beyondSouth = !south && y >= 16 - southDepth;
    if (!(beyondWest || beyondEast || beyondNorth || beyondSouth)) return true;
    // Filled diagonal corners only exist when that diagonal neighbor does.
    if (beyondWest && beyondNorth) return nw;
    if (beyondEast && beyondNorth) return ne;
    if (beyondEast && beyondSouth) return se;
    if (beyondWest && beyondSouth) return sw;
    return false;
  };
  const landAt = (x, y) => {
    if (x >= 0 && x < 16 && y >= 0 && y < 16) return occupiedInside(x, y);
    const cx = x < 0 ? -1 : x > 15 ? 1 : 0;
    const cy = y < 0 ? -1 : y > 15 ? 1 : 0;
    if (cx && cy) return cx < 0 ? (cy < 0 ? nw : sw) : (cy < 0 ? ne : se);
    const lx = ((x % 16) + 16) % 16;
    const ly = ((y % 16) + 16) % 16;
    if (cx) {
      if (!(cx < 0 ? west : east)) return false;
      if ((cx < 0 ? !nw : !ne) && ly < SHORE_INSET[lx]) return false;
      if ((cx < 0 ? !sw : !se) && ly >= 16 - SHORE_INSET[lx]) return false;
      return true;
    }
    if (!(cy < 0 ? north : south)) return false;
    if ((cy < 0 ? !nw : !sw) && lx < SHORE_INSET[ly]) return false;
    if ((cy < 0 ? !ne : !se) && lx >= 16 - SHORE_INSET[ly]) return false;
    return true;
  };

  // Pad covers ring probes of radius CONTACT_INSET+2 plus the 1px contact halo.
  const pad = CONTACT_INSET + 3;
  const origin = pad;
  const size = 16 + pad * 2;
  const land = new Uint8Array(size * size);
  const water = new Uint8Array(size * size);
  for (let y = -pad; y < 16 + pad; y++) {
    for (let x = -pad; x < 16 + pad; x++) {
      const i = (y + origin) * size + (x + origin);
      const isLand = landAt(x, y);
      land[i] = isLand ? 1 : 0;
      water[i] = isLand ? 0 : 1;
    }
  }
  const distToWater = chebyshevDistance(water, size, size);
  const distToLand = chebyshevDistance(land, size, size);
  const at = (field, x, y) => field[(y + origin) * size + (x + origin)];

  const occupied = new Uint8Array(256);
  const contact = new Uint8Array(256);
  const landDist = new Uint8Array(256);
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const i = y * 16 + x;
      occupied[i] = occupiedInside(x, y) ? 1 : 0;
      landDist[i] = at(distToLand, x, y);
      if (!occupied[i] || at(distToWater, x, y) !== CONTACT_INSET) continue;
      contact[i] = NEIGHBOURS.some(([dx, dy]) => at(distToWater, x + dx, y + dy) > CONTACT_INSET) ? 1 : 0;
    }
  }
  return { occupied, contact, landDist };
}

const SHORE_FIELDS = new Map(BLOB_MASKS.map((mask) => [mask, shoreFields(mask)]));

function beachVariants(phase) {
  const bandDepth = SHORE_BAND[phase];
  return Object.fromEntries(BLOB_MASKS.map((mask) => {
    const { occupied, contact, landDist } = SHORE_FIELDS.get(mask);
    const pixels = Array.from({ length: 16 }, (_, y) => Array.from({ length: 16 }, (_, x) => {
      const i = y * 16 + x;
      if (occupied[i]) {
        // Flat sand with one contact line inset from the waterline on the land
        // side. A fully connected interior has no waterline and stays flat.
        return contact[i] ? EARTH_DARK : SAND;
      }
      // Lit shore band on the water side; the body and depth bands belong to
      // the water backdrop underneath.
      return landDist[i] <= bandDepth ? WATER_CREST : 0;
    }));
    return [mask, pixels];
  }));
}

function animatedBeachTile() {
  const frames = SHORE_BAND.map((_, phase) => beachVariants(phase));
  return {
    key: 'foundation.sand-coast', name: 'Sand coastline', channel: 'foundation', mode: 'blob8', connectionGroup: 'landmass',
    palette: ART_PALETTE, collision: { ground: false },
    components: { ground: cloneVariants(frames[0]) },
    animation: { component: 'ground', frameDurationMs: 220, variantFrames: frames },
  };
}

export const WATER_BACKDROP = {
  key: 'backdrop.water', name: 'Water backdrop', channel: 'backdrop', mode: 'plain',
  connectionGroup: 'water', palette: ART_PALETTE, collision: {},
  components: { ground: { 0: clonePixels(WATER_FRAMES[0]) } }, backdropColor: '#4fa4b8ff',
  animation: { component: 'ground', frameDurationMs: 220, frames: WATER_FRAMES },
};

export const SAND_COAST = { ...animatedBeachTile(), autoFoundation: true };
